import type {
	CoursesMarkLessonCompleteDto,
	CoursesSaveLessonPositionDto,
	CoursesSubmitJournalEntryDto,
	CoursesSubmitQuizDto,
} from '@sportspredict/shared';
import type { Wire } from '../../types/common.types';
import { coursesApi } from './courses-base.api';
import type {
	MarkLessonCompleteInput,
	MarkLessonCompleteOutput,
	MarkLessonStartedInput,
	MarkLessonStartedOutput,
	SaveLessonPositionInput,
	SaveLessonPositionOutput,
	GetLessonPositionInput,
	GetLessonPositionOutput,
	GetQuizInput,
	GetQuizOutput,
	SubmitQuizInput,
	SubmitQuizOutput,
	GetJournalEntryInput,
	GetJournalEntryOutput,
	SubmitJournalEntryInput,
	SubmitJournalEntryOutput,
} from '../../types/lessons.types';

const TAG = 'courses:lessons' as const;
const INVALIDATES = [TAG, 'courses:modules', 'courses:courses', 'courses:dashboard'] as const;

export const lessonsEndpoints = coursesApi.injectEndpoints({
	endpoints: (builder) => ({
		markLessonComplete: builder.mutation<MarkLessonCompleteOutput, MarkLessonCompleteInput>({
			query: ({ lessonId, ...body }) => ({
				url: `courses/lessons/${encodeURIComponent(lessonId)}/complete`,
				method: 'POST',
				body: body satisfies Wire<CoursesMarkLessonCompleteDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		markLessonStarted: builder.mutation<MarkLessonStartedOutput, MarkLessonStartedInput>({
			query: ({ lessonId }) => ({
				url: `courses/lessons/${encodeURIComponent(lessonId)}/started`,
				method: 'POST',
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		saveLessonPosition: builder.mutation<SaveLessonPositionOutput, SaveLessonPositionInput>({
			query: ({ lessonId, ...body }) => ({
				url: `courses/lessons/${encodeURIComponent(lessonId)}/position`,
				method: 'PUT',
				body: body satisfies Wire<CoursesSaveLessonPositionDto>,
			}),
			// A position write changes no progress data and `useLessonPosition` patches the
			// position cache itself; invalidating would refetch the lesson, module, quiz and
			// journal on every debounced save while reading.
			invalidatesTags: [],
		}),
		getLessonPosition: builder.query<GetLessonPositionOutput, GetLessonPositionInput>({
			query: ({ lessonId }) => ({
				url: `courses/lessons/${encodeURIComponent(lessonId)}/position`,
			}),
			providesTags: [TAG],
		}),
		getQuiz: builder.query<GetQuizOutput, GetQuizInput>({
			query: ({ lessonId }) => ({ url: `courses/lessons/${encodeURIComponent(lessonId)}/quiz` }),
			providesTags: [TAG],
		}),
		submitQuiz: builder.mutation<SubmitQuizOutput, SubmitQuizInput>({
			query: ({ lessonId, ...body }) => ({
				url: `courses/lessons/${encodeURIComponent(lessonId)}/quiz/submit`,
				method: 'POST',
				body: body satisfies Wire<CoursesSubmitQuizDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		getJournalEntry: builder.query<GetJournalEntryOutput, GetJournalEntryInput>({
			query: ({ lessonId }) => ({ url: `courses/lessons/${encodeURIComponent(lessonId)}/journal` }),
			providesTags: [TAG],
		}),
		submitJournalEntry: builder.mutation<SubmitJournalEntryOutput, SubmitJournalEntryInput>({
			query: ({ lessonId, ...body }) => ({
				url: `courses/lessons/${encodeURIComponent(lessonId)}/journal`,
				method: 'POST',
				body: body satisfies Wire<CoursesSubmitJournalEntryDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
	}),
});

export const {
	useMarkLessonCompleteMutation,
	useMarkLessonStartedMutation,
	useSaveLessonPositionMutation,
	useGetLessonPositionQuery,
	useLazyGetLessonPositionQuery,
	useGetQuizQuery,
	useLazyGetQuizQuery,
	useSubmitQuizMutation,
	useGetJournalEntryQuery,
	useLazyGetJournalEntryQuery,
	useSubmitJournalEntryMutation,
} = lessonsEndpoints;
